import React, { createContext, useContext, useState, useEffect } from 'react';
import { handleSSOToken } from '../services/auth';
import {
  clearSessionIfCurrent,
  getToken,
  getUser,
  isAuthenticated as hasStoredToken,
  logout as clearStoredSession,
  SESSION_INVALID_EVENT_NAME,
  setToken,
  setUser as storeUser,
  validateSession,
} from '../services/authSession';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => (
    hasStoredToken() ? getUser() : null
  ));
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => (
    hasStoredToken() && !!getUser()
  ));

  useEffect(() => {
    const ssoRedirect = handleSSOToken();
    if (ssoRedirect) {
      const storedUser = getUser();
      if (storedUser) {
        setUser(storedUser);
        setIsAuthenticated(true);
        setTimeout(() => {
          window.location.href = ssoRedirect;
        }, 100);
      }
    }

    const handleInvalidSession = () => {
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener(SESSION_INVALID_EVENT_NAME, handleInvalidSession);

    const token = getToken();
    if (!token) {
      setLoading(false);
      return () => window.removeEventListener(
        SESSION_INVALID_EVENT_NAME,
        handleInvalidSession
      );
    }

    let active = true;
    validateSession(token).then((result) => {
      if (!active || getToken() !== token) return;

      if (result.status === 200) {
        storeUser(result.user);
        setUser(result.user);
        setIsAuthenticated(true);
      } else if (result.status === 401) {
        clearSessionIfCurrent(token);
      }
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
      window.removeEventListener(SESSION_INVALID_EVENT_NAME, handleInvalidSession);
    };
  }, []);

  const login = (userData, accessToken) => {
    storeUser(userData);
    setToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    clearStoredSession();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
